import Live from './Live.js'

// Must match CLOUDFRONT_DOMAINS in plug/file/api.py so a file always maps to the same distribution
const CLOUDFRONT_DOMAINS = ['d36szuo3i5x415.cloudfront.net', 'd2rojxgsddvahi.cloudfront.net'];

let getQuery = function(args) {
    var query = '';
    args = args || {};

    if (args) {
        var pairs = Object.entries(args).map( ([key, value]) => key + "=" + (value == undefined ? '' : encodeURIComponent(value) ) );
        if (pairs.length) {
            query = '?' + pairs.join('&');
        }
    }

    return query;
};

class ZoeziApi { // Accessible as $api or window.$zoeziapi
    post(path, data, args) {
        return new Promise((resolve) => {
            const hostname = this.$root.siteconfig?.hostname || location.hostname;
            fetch(`${location.protocol}//${hostname}${path}${getQuery(args)}`,
                {
                    method: 'POST',
                    headers: data instanceof FormData ? undefined : { 'CONTENT-TYPE': 'application/json' },
                    body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
                    credentials: 'include'
                }).then(resolve).catch(() => {resolve({ok: false, json: () => Promise.resolve({message: 'An error occurred'})});})
        });
    }

    async get(path, args, options = {}) {
        const hostname = this.$root.siteconfig?.hostname || location.hostname;
        const url = `${location.protocol}//${hostname}${path}${getQuery(args)}`;

        const response = await fetch(url, { method: 'GET', credentials: 'include' });
        
        if (options.blob) {
            return await response.blob();
        }

        if (!response.ok) {
            // In order to be able to ignore the error in Sentry code
            if (path == '/api/memberapi/get/current') {
                throw new Error('ZoeziAPI: Not logged in');
            }

            throw response;
        }

        return await response.json();
    }

    callStaffApp(module, fn) {
        return new Promise((resolve) => {
            let callbackId = ++this.lastCallbackId;
            this.callbacks[callbackId] = (data) => {
                delete this.callbacks[callbackId];
                resolve(data);
            };
            var args = Array.prototype.slice.call(arguments).slice(2);
            window.parent.postMessage({module, fn, args, callbackId});
        });
    }

    getSettings() {
        return fetch(`${location.protocol}//${location.hostname}/api/public/settings/get`, {method: 'GET', credentials: 'include'}).then(response => {
            if (!response.ok) {
                throw {};
            }
            return response.ok && response.json();
        });
    }

    getImageUrl(key, size, option) {
        if (key && typeof key === 'string' && key.startsWith('/assets')) {
            return this.getAssetUrl(key);
        }

        if (!key || (typeof key === 'string' && (key.startsWith('http') || key.startsWith('/')))) {
            return key;
        }

        const hostname = this.$root.siteconfig?.hostname || location.hostname;
        // Settings may not be loaded yet when images render; guess from the hostname until they are
        let useCloudFront = this.$root.$store.state?.settings?.useCloudFront
            ?? !/(zoezidev\.se|zoezitest\.com|zoezistaging\.com|zoezisandbox\.com|localhost)$/.test(hostname);

        if (typeof key === 'object') {
            if (key.key) {
                let file = key;
                key = file.key;
                if (useCloudFront) return this.cloudfrontUrl(hostname, key, size);
            } else if (key.imagekey) {
                key = key.imagekey;
            } else {
                return key.imageurl;
            }
        }

        if (useCloudFront) return this.cloudfrontUrl(hostname, key, size);
        let href = `${location.protocol}//${hostname}/api/utils/file/download?key=${key}`;
        if (size) {
            href += `&size=${size}`;
        }
        if (option?.notrack) {
            href += `&notrack`;
        }

        return href;
    }

    cloudfrontUrl(host, key, size) {
        // Spread files over the distributions by key so each file is cached in only one of them
        const cloudfront = CLOUDFRONT_DOMAINS[key.charCodeAt(key.length - 1) % CLOUDFRONT_DOMAINS.length];
        let url = `https://${cloudfront}/${host}/?key=${key}`;
        if (size) {
            for (let allowedSize of this.thumbnailSizes) {
                if (size <= allowedSize) {
                    url += `&size=` + allowedSize;
                    break;
                }
            }
        }
        return url;
    }

    hasFlag(flag) {
        return !!window.FLAG_DATA[flag];
    }

    getAssetUrl(path) {
        const hostname = this.$root.siteconfig?.hostname || location.hostname;
        return `${location.protocol}//${hostname}${path}`;
    }

    subscribe(option) {
        return this.live.subscribe(option);
    }

    subscribeChannel(channel, fn) {
        return this.live.subscribeChannel(channel, fn);
    }

    startWebSocket() {
        const hostname = this.$root.siteconfig?.hostname || location.hostname;
        console.log('Starting websocket to ' + hostname);
        if (this.live) {
            this.live.stop();
        }
        this.live = new Live();
        this.live.start({url: 'wss://' + hostname + '/api/async?type=public', $root: this.$root});
    }

    reloadSettings() {
        this.getSettings().then(result => {
            this.$root.$store.commit('setSettings', result);
        });
    }

    setVueRoot($root) {
        this.$root = $root;
        $root.$watch('siteconfig.hostname', (newVal, oldVal) => {
            console.log('ZoeziApi detected change of hostname from ' + oldVal + ' to ' + newVal);
            this.startWebSocket();
        })

        this.startWebSocket();

        this.$root.$watch('$store.state.isLoggedIn', () => {
            if (this.$root.$store.state.user) {
                this.live.authenticate(this.$root.$store.state.user.session, 'isLoggedIn');
            }
        });

        this.$root.$watch('$store.state.isConnected', () => {
            if (this.$root.$store.state.isConnected && this.$root.$store.state.user) {
                this.live.authenticate(this.$root.$store.state.user.session, 'isConnected');
            }
        });

        this.subscribe({type: '#event', action: 'settings_changed', callback: () => {
            this.reloadSettings();
        }})

        this.subscribe({type: 'flag', callback: (_, event) => {
            const flags = this.$root.$store.state?.settings?.flags;
            if (!flags) return;

            const action = event.action;
            const flag = event.data.flag;

            if (action === 'remove') {
                this.$root.$store.state.settings.flags = flags.filter(f => f !== flag);
            } else if (action === 'add' && !flags.includes(flag)) {
                flags.push(flag);
            }
        }})

        this.subscribe({type: 'addon', callback: () => {
            this.reloadSettings();
        }})

        this.subscribe({type: 'coursecategory', callback: () => {
            this.reloadSettings();
        }})

        this.reloadSettings();

        this.lastCallbackId = 0;
        this.callbacks = {};

        window.addEventListener('message', (event) => {
            if (event.source && event.source.location && event.source.location == window.parent.location) {
                if (event.data.callbackId) {
                    let callback = this.callbacks[event.data.callbackId];
                    if (callback) {
                        callback(event.data.data);
                    }
                }
            }
        });
    }

    hasPermission(task) {
        return this.$root.$store.state.user && (!task || this.$root.$store.state.user.permissions.indexOf(task) != -1);
    }

    constructor(Vue) {
        Vue.prototype.$api = this;

        window.$zoeziapi = this;

        this.thumbnailSizes = [100, 300, 600, 960, 1280, 1600, 1920, 2560, 3840];  // images are loaded earlier than settings
    }
}

export default {
    install(Vue) {
        new ZoeziApi(Vue);

        const fn = Vue.prototype.$mount;
        Vue.prototype.$mount = function() {
            let res = fn.apply(this, arguments);
            if (this.$options && this.$options.name?.startsWith("zoezi-")) {
                if (this.$el.classList) {
                    this.$el.classList.add(this.$options.name);
                }
            }

            return res;
        }
    }
}
